/**
 * ConfirmationModal — confirm before the one irreversible terminal action.
 *
 * Spec: docs/superpowers/specs/2026-05-31-the-feed-design.md §3 (Contract 3:
 * the terminal send/approve/merge is ONE explicit, deliberate control).
 *
 * The terminal control never fires from free text — it always routes through a
 * deliberate confirm. This modal is that gate. Editorial skin: cream card,
 * serif headline, mono caption, amber confirm.
 */
import { useEffect, type CSSProperties } from "react";

export interface ConfirmationModalProps {
  open: boolean;
  /** Mono caption above the headline ("Approve" / "Reject" / "Commit"). */
  caption: string;
  /** Serif headline question. */
  title: string;
  /** Optional body copy. */
  body?: string;
  /** Confirm-button label. */
  confirmLabel: string;
  /** When true the confirm button renders in the destructive (reject) tone. */
  destructive?: boolean;
  /** Disables the confirm button while the POST is in flight. */
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  zIndex: 60,
  display: "grid",
  placeItems: "center",
  // Scrim from the canonical ink token (was the old newsroom ink #1a1410).
  background: "color-mix(in oklab, var(--ink) 32%, transparent)",
  padding: 24,
};

export function ConfirmationModal({
  open,
  caption,
  title,
  body,
  confirmLabel,
  destructive = false,
  pending = false,
  onConfirm,
  onCancel,
}: ConfirmationModalProps) {
  // Esc cancels.
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onCancel();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  if (!open) return null;

  const confirmColor = destructive ? "var(--error)" : "var(--accent)";

  return (
    <div
      style={overlay}
      data-testid="feed-confirm-overlay"
      onClick={onCancel}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        data-testid="feed-confirm-modal"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-lg"
        style={{
          background: "var(--paper-card)",
          border: "1px solid var(--rule)",
          boxShadow: "0 24px 60px color-mix(in oklab, var(--ink) 22%, transparent)",
          padding: "22px 24px",
        }}
      >
        <div
          style={{
            fontFamily: "var(--mono)",
            fontSize: 9.5,
            letterSpacing: "0.24em",
            textTransform: "uppercase",
            color: "var(--accent-warm)",
          }}
        >
          {caption}
        </div>
        <h2
          style={{
            // Serif headline (the modal IS a reading moment); was --display (Sora)
            // carrying inert display variation axes — both removed.
            fontFamily: "var(--font-serif)",
            fontWeight: 600,
            fontSize: 20,
            lineHeight: 1.25,
            color: "var(--ink)",
            margin: "6px 0 0",
          }}
        >
          {title}
        </h2>
        {body && (
          <p
            className="mt-2"
            style={{
              fontFamily: "var(--body)",
              fontSize: 14,
              color: "var(--ink-faded)",
              lineHeight: 1.5,
            }}
          >
            {body}
          </p>
        )}
        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={pending}
            data-testid="feed-confirm-cancel"
            style={{
              fontFamily: "var(--body)",
              fontSize: 13.5,
              fontWeight: 500,
              padding: "7px 14px",
              borderRadius: 8,
              border: "1px solid var(--rule)",
              background: "transparent",
              color: "var(--ink-soft)",
              cursor: pending ? "default" : "pointer",
              opacity: pending ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={pending}
            data-testid="feed-confirm-confirm"
            style={{
              fontFamily: "var(--body)",
              fontSize: 13.5,
              fontWeight: 600,
              padding: "7px 16px",
              borderRadius: 8,
              border: "none",
              background: confirmColor,
              color: "var(--paper-bright)",
              cursor: pending ? "default" : "pointer",
              opacity: pending ? 0.7 : 1,
            }}
          >
            {pending ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
